import { useState } from 'react'
import { Flex, VStack, Heading, Text, Box, MenuItem } from '@chakra-ui/react'
import { motion } from 'framer-motion'
import { FaStar, FaRegStar } from 'react-icons/fa'
import { Folder } from '@/types'
import { listItemVariants } from '@/theme/animations'
import { useAppTranslation } from '@/contexts/LanguageContext'
import EditFolderModal from '@/components/Modals/EditFolderModal'
import DeleteFolderModal from '@/components/Modals/DeleteFolderModal'
import FolderPermissionsModal from '@/components/Modals/FolderPermissionsModal'
import HeaderActions from '@/components/FileExplorer/HeaderActions'
import { useFavorites } from '@/contexts/FavoritesContext'
import { useNavigation } from '@/hooks/useNavigation'
import Breadcrumb from '@/components/Common/Breadcrumb'
import { apiService } from '@/services/api'

const MotionBox = motion.div

interface FolderHeaderProps {
  folder: Folder
  onUpload: () => void
  onCreateFolder: () => void
  onFolderUpdate?: (updatedFolder: Folder) => void
  onFolderDelete?: (folderId: number) => void
  onFilesChange?: () => void
  onFolderTreeChange?: () => void
}

export default function FolderHeader({
  folder,
  onUpload,
  onCreateFolder,
  onFolderUpdate,
  onFolderDelete,
  onFilesChange,
  onFolderTreeChange,
}: FolderHeaderProps) {
  const { t } = useAppTranslation(['common', 'files'])
  const [isEditModalOpen, setIsEditModalOpen] = useState(false)
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false)
  const [isPermissionsModalOpen, setIsPermissionsModalOpen] = useState(false)
  const { toggleFavorite, isFavorite } = useFavorites()
  const { folderTree, selectFolder } = useNavigation()
  const isFolderFavorite = isFavorite('folder', folder.id)

  const handleFolderUpdate = (updatedFolder: Folder) => {
    onFolderUpdate?.(updatedFolder)
  }

  const handleFolderDelete = (folderId: number) => {
    onFolderDelete?.(folderId)
  }

  const handleToggleFavorite = async () => {
    try {
      await toggleFavorite('folder', folder)
    } catch (error) {
      console.error('Failed to toggle favorite:', error)
    }
  }

  const handleDownloadFolder = () => {
    const downloadUrl = apiService.getFolderDownloadZipUrl(folder.id)
    window.open(downloadUrl, '_blank')
  }

  return (
    <>
      <MotionBox variants={listItemVariants}>
        <VStack align="start" spacing={2} mb={2}>
          {/* Breadcrumb Row */}
          <Breadcrumb path={folder.path} folderTree={folderTree} onFolderSelect={selectFolder} />

          {/* Title row, then description + actions */}
          <VStack align="stretch" spacing={2} w="full">
            {/* Wraps below `lg`, where the actions claim the full width and drop below the name */}
            <Flex align="center" gap={3} w="full" wrap="wrap">
              <Box
                cursor="pointer"
                onClick={handleToggleFavorite}
                color={isFolderFavorite ? 'yellow.400' : 'gray.300'}
                flexShrink={0}
                _hover={{
                  color: isFolderFavorite ? 'yellow.500' : 'yellow.400',
                  transform: 'scale(1.1)',
                }}
                transition="all 0.2s"
              >
                {isFolderFavorite ? <FaStar size="20px" /> : <FaRegStar size="20px" />}
              </Box>
              <Heading
                size="md"
                color="gray.800"
                fontWeight="bold"
                noOfLines={2}
                wordBreak="break-word"
                flex="1"
                minW={0}
              >
                {folder.name}
              </Heading>

              <HeaderActions
                folder={folder}
                onFilesChange={onFilesChange}
                onFolderTreeChange={onFolderTreeChange}
                menuItems={
                  <>
                    <MenuItem onClick={onUpload}>
                      {t('fileExplorer.uploadFiles', { ns: 'files' })}
                    </MenuItem>
                    <MenuItem onClick={onCreateFolder}>
                      {t('fileExplorer.createFolder', { ns: 'files' })}
                    </MenuItem>
                    <MenuItem onClick={handleDownloadFolder}>
                      {t('folders.downloadAsZip', { ns: 'files' })}
                    </MenuItem>
                    <MenuItem onClick={() => setIsPermissionsModalOpen(true)}>
                      {t('folderPermissions.title', { ns: 'files' })}
                    </MenuItem>
                    <MenuItem onClick={() => setIsEditModalOpen(true)}>
                      {t('folders.renameFolder', { ns: 'files' })}
                    </MenuItem>
                    <MenuItem onClick={() => setIsDeleteModalOpen(true)} color="red.500">
                      {t('folders.deleteFolder', { ns: 'files' })}
                    </MenuItem>
                  </>
                }
              />
            </Flex>

            <Text
              minW={0}
              color="gray.600"
              fontSize="sm"
              noOfLines={2}
              lineHeight="short"
              title={
                folder.description?.trim() || t('folderHeader.defaultDescription', { ns: 'files' })
              }
            >
              {folder.description || t('folderHeader.defaultDescription', { ns: 'files' })}
            </Text>
          </VStack>
        </VStack>
      </MotionBox>

      {/* Edit Folder Modal */}
      <EditFolderModal
        isOpen={isEditModalOpen}
        onClose={() => setIsEditModalOpen(false)}
        folder={folder}
        onUpdate={handleFolderUpdate}
      />

      {/* Delete Folder Modal */}
      <DeleteFolderModal
        isOpen={isDeleteModalOpen}
        onClose={() => setIsDeleteModalOpen(false)}
        folder={folder}
        onDelete={handleFolderDelete}
      />

      {/* Folder Permissions Modal */}
      <FolderPermissionsModal
        isOpen={isPermissionsModalOpen}
        onClose={() => setIsPermissionsModalOpen(false)}
        folderId={folder.id}
        folderName={folder.name}
      />
    </>
  )
}
